import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { PollQuestionType } from '../../../../../../api/types/settings/settings-all';
import { createQuestionOperation } from '../operations/create-question-operation';

interface AddQuestionDialogProps {
  pollId: number;
  closeDialog: () => void;
}

interface TempOption {
  id: number;
  optionText: string;
  optionValue: number;
  optionOrder: number;
}

const questionTypeOptions = [
  { label: 'טקסט חופשי', value: PollQuestionType.free_text },
  { label: 'בחירה מרובה', value: PollQuestionType.multiple_choice },
];

export const AddQuestionDialog: React.FC<AddQuestionDialogProps> = ({
  pollId,
  closeDialog,
}) => {
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState<PollQuestionType>(
    PollQuestionType.free_text,
  );
  const [options, setOptions] = useState<TempOption[]>([]);
  const [newOptionText, setNewOptionText] = useState('');
  const [newOptionValue, setNewOptionValue] = useState<number | null>(1);
  const [loading, setLoading] = useState(false);
  const [editingOptionId, setEditingOptionId] = useState<number | null>(null);
  const [editingOptionText, setEditingOptionText] = useState('');
  const [editingOptionValue, setEditingOptionValue] = useState<number | null>(
    null,
  );

  const addOption = () => {
    if (newOptionText.trim().length === 0) return;
    const newOption: TempOption = {
      id: Date.now(),
      optionText: newOptionText.trim(),
      optionValue: newOptionValue ?? options.length + 1,
      optionOrder: options.length + 1,
    };
    setOptions([...options, newOption]);
    setNewOptionText('');
    setNewOptionValue(options.length + 2);
  };

  const removeOption = (optionId: number) => {
    const filtered = options.filter((opt) => opt.id !== optionId);
    // Reorder remaining options (keep values, just update order)
    const reordered = filtered.map((opt, index) => ({
      ...opt,
      optionOrder: index + 1,
    }));
    setOptions(reordered);
  };

  const startEditOption = (option: TempOption) => {
    setEditingOptionId(option.id);
    setEditingOptionText(option.optionText);
    setEditingOptionValue(option.optionValue);
  };

  const saveEditOption = () => {
    if (editingOptionId === null) return;
    setOptions(
      options.map((opt) =>
        opt.id === editingOptionId
          ? {
              ...opt,
              optionText: editingOptionText.trim(),
              optionValue: editingOptionValue ?? opt.optionValue,
            }
          : opt,
      ),
    );
    setEditingOptionId(null);
    setEditingOptionText('');
    setEditingOptionValue(null);
  };

  const cancelEditOption = () => {
    setEditingOptionId(null);
    setEditingOptionText('');
    setEditingOptionValue(null);
  };

  const handleSubmit = async () => {
    const formattedOptions =
      questionType === PollQuestionType.multiple_choice
        ? options.map((opt) => ({
            optionText: opt.optionText,
            optionValue: opt.optionValue,
            optionOrder: opt.optionOrder,
          }))
        : undefined;

    await createQuestionOperation(
      pollId,
      questionText,
      questionType,
      formattedOptions,
      setLoading,
      closeDialog,
    );
  };

  const isValid =
    questionText.trim().length >= 2 &&
    (questionType === PollQuestionType.free_text || options.length > 0);

  return (
    <div style={{ direction: 'rtl' }}>
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 500,
            color: '#374151',
          }}
        >
          טקסט השאלה
        </label>
        <InputText
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          placeholder="הזן את טקסט השאלה"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 500,
            color: '#374151',
          }}
        >
          סוג השאלה
        </label>
        <Dropdown
          value={questionType}
          options={questionTypeOptions}
          onChange={(e) => {
            setQuestionType(e.value);
            if (e.value === PollQuestionType.free_text) {
              setOptions([]);
            }
          }}
          placeholder="בחר סוג שאלה"
          style={{ width: '100%' }}
        />
      </div>

      {questionType === PollQuestionType.multiple_choice && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 500,
              color: '#374151',
            }}
          >
            תשובות אפשריות
          </label>

          {options.length > 0 && (
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
                backgroundColor: '#f9fafb',
              }}
            >
              {options.map((option, index) => (
                <div
                  key={option.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    marginBottom: index < options.length - 1 ? '8px' : 0,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <span
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    {index + 1}
                  </span>

                  {editingOptionId === option.id ? (
                    <>
                      <InputText
                        value={editingOptionText}
                        onChange={(e) => setEditingOptionText(e.target.value)}
                        style={{ flex: 1 }}
                        placeholder="טקסט התשובה"
                        autoFocus
                      />
                      <InputNumber
                        value={editingOptionValue}
                        onValueChange={(e) =>
                          setEditingOptionValue(e.value ?? 0)
                        }
                        style={{ width: '80px' }}
                        placeholder="ערך"
                        min={0}
                      />
                      <Button
                        icon="pi pi-check"
                        className="p-button-rounded p-button-success p-button-text"
                        onClick={saveEditOption}
                        style={{ width: '32px', height: '32px' }}
                      />
                      <Button
                        icon="pi pi-times"
                        className="p-button-rounded p-button-secondary p-button-text"
                        onClick={cancelEditOption}
                        style={{ width: '32px', height: '32px' }}
                      />
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1 }}>{option.optionText}</span>
                      <span
                        style={{
                          backgroundColor: '#dbeafe',
                          color: '#1e40af',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        ערך: {option.optionValue}
                      </span>
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-text p-button-warning"
                        onClick={() => startEditOption(option)}
                        style={{ width: '32px', height: '32px' }}
                        tooltip="עריכה"
                        tooltipOptions={{ position: 'top' }}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-text p-button-danger"
                        onClick={() => removeOption(option.id)}
                        style={{ width: '32px', height: '32px' }}
                        tooltip="מחיקה"
                        tooltipOptions={{ position: 'top' }}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: '8px',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '12px',
                  color: '#6b7280',
                }}
              >
                טקסט התשובה
              </label>
              <InputText
                value={newOptionText}
                onChange={(e) => setNewOptionText(e.target.value)}
                placeholder="הוסף תשובה חדשה"
                style={{ width: '100%' }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOption();
                  }
                }}
              />
            </div>
            <div style={{}}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '12px',
                  color: '#6b7280',
                }}
              >
                ערך (ציון)
              </label>
              <InputNumber
                value={newOptionValue}
                onValueChange={(e) => setNewOptionValue(e.value ?? 0)}
                style={{ width: '100%' }}
                placeholder="ערך"
                min={0}
              />
            </div>
            <Button
              icon="pi pi-plus"
              onClick={addOption}
              tooltip={
                newOptionText.trim().length === 0
                  ? 'יש למלא את התשובה הראשונה'
                  : 'הוספת תשובה'
              }
              tooltipOptions={{ showOnDisabled: true }}
              disabled={newOptionText.trim().length === 0}
              style={{ height: '36px', marginTop: '16px' }}
            />
          </div>

          {options.length === 0 && (
            <p
              style={{
                color: '#9ca3af',
                fontSize: '0.875rem',
                marginTop: '8px',
              }}
            >
              יש להוסיף לפחות תשובה אחת לשאלת בחירה מרובה
            </p>
          )}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb',
        }}
      >
        <Button
          label="ביטול"
          icon="pi pi-times"
          className="p-button-outlined p-button-secondary"
          onClick={closeDialog}
          disabled={loading}
        />
        <Button
          label="יצירת שאלה"
          icon="pi pi-check"
          onClick={handleSubmit}
          loading={loading}
          disabled={!isValid}
        />
      </div>
    </div>
  );
};
